import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { EMPTY, switchMap } from 'rxjs';
import { Memoize } from 'typescript-memoize';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { BillingPlanTier } from '@core/billing-types';
import { filterDefined } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { JobEditorDialogPathsMap, JobEditorDialogTab } from '@core/offers-types';
import { Step, WalkthroughState, WalkthroughStepsService } from '@core/walkthrough-legacy';
import { SubscriptionDataService } from '@recruitee/billing-logic';

import { GrowAutomationsWalkthroughStepBuilder } from './step-builders/grow-automations-walkthrough-step-builder';
import { OptimizeAutomationsWalkthroughStepBuilder } from './step-builders/optimize-automations-walkthrough-step-builder';
import { StageItemAutomationsWalkthroughStepBuilder } from './step-builders/stage-item-automations-walkthrough-step-builder';
import { StartAutomationsWalkthroughStepBuilder } from './step-builders/start-automations-walkthrough-step-builder';

@Injectable({ providedIn: 'root' })
export class AutomationsWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly subscriptionDataService = inject(SubscriptionDataService);
  private readonly hasManageAA = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageAutomatedActions),
  );
  private readonly currentUser = this.store.selectSignal(CurrentUserSelectors.data);

  private readonly hasWorkflowAutomations = injectDevFlag(DevFlag.WorkflowAutomations);

  private readonly optimizeStepBuilder = new OptimizeAutomationsWalkthroughStepBuilder();
  private readonly growStepBuilder = new GrowAutomationsWalkthroughStepBuilder();
  private readonly startStepBuilder = new StartAutomationsWalkthroughStepBuilder();
  private readonly workflowStepBuilder = new StageItemAutomationsWalkthroughStepBuilder();

  private readonly workflowSteps = this.workflowStepBuilder.build();

  private stepsMap: Record<BillingPlanTier, Step[]> = {
    [BillingPlanTier.Grow]: this.growStepBuilder.build(),
    [BillingPlanTier.Scale]: this.startStepBuilder.build(),
    [BillingPlanTier.Start]: this.startStepBuilder.build(),
    [BillingPlanTier.Lead]: this.startStepBuilder.build(),
    [BillingPlanTier.Launch]: [],
    [BillingPlanTier.Optimize]: this.optimizeStepBuilder.build(),
  };

  public start(): void {
    if (
      this.hasWorkflowAutomations &&
      this.currentUser() &&
      !this.currentUser()!.featureDiscovery.workflowAutomationsUsed
    ) {
      this.subscriptionDataService.data$
        .pipe(
          filterDefined,
          switchMap(subscription => {
            const steps = this.stepsMap[subscription.tier];
            if (!steps.length) {
              return EMPTY;
            }
            return this.walkthroughStepsService.start(
              this.getFilteredSteps(this.hasManageAA(), steps),
            );
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(walkthroughState => {
          if ([WalkthroughState.Finished, WalkthroughState.Canceled].includes(walkthroughState)) {
            this.store.dispatch(
              FeatureDiscoveryActions.update({ payload: { workflowAutomationsUsed: true } }),
            );
          }
        });
    }
  }

  public startWorkflow() {
    if (!this.hasWorkflowAutomations) return;

    // Scroll to the top of the list to be sure the first step is visible
    document
      .querySelector('rt-components-list')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    this.walkthroughStepsService
      .start(this.workflowSteps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  @Memoize()
  public getFilteredSteps(condition: boolean, steps: Step[]): Step[] {
    return steps.filter(step => condition || step.position === 1);
  }

  public navigateToSecondStep(jobId: number, inTemplateEditor?: boolean) {
    if (this.hasManageAA()) {
      this.router.navigate(
        this.getPathToNewJobEditor(jobId, JobEditorDialogTab.Process, inTemplateEditor),
      );
    }
  }

  private getPathToNewJobEditor(
    jobId: number,
    tab: JobEditorDialogTab,
    inTemplateEditor?: boolean,
  ) {
    const outlet = inTemplateEditor ? 'job-template-editor' : 'job-editor';
    return [
      {
        outlets: {
          [outlet]: [jobId, JobEditorDialogPathsMap[tab]],
        },
      },
    ];
  }
}
